// ============================================
// VOLUNTEER MODULE - ATTENDANCE PAGE
// Fully themed via useTheme() — no hardcoded colors
// ============================================

import React, { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeContext";
import { getAttendanceForDate, importAttendance, downloadAttendanceTemplate } from "../services/api";

export default function Attendance() {
  const { C } = useTheme();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [attendance, setAttendance]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [importing, setImporting]       = useState(false);
  const [message, setMessage]           = useState(null);

  useEffect(() => { load(); }, [selectedDate]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAttendanceForDate(selectedDate);
      setAttendance(res.data.attendance || []);
    } catch { setMessage({ type:"error", text:"Failed to load attendance." }); }
    setLoading(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if(!file) return;
    setImporting(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await importAttendance(formData);
      setMessage({ type:"success", text:res.data.message });
      load();
    } catch { setMessage({ type:"error", text:"Import failed. Please check the file format." }); }
    setImporting(false);
    e.target.value = "";
  };

  const statusColor = (s) => ({ Present:C.green, Absent:C.red, Late:C.orange }[s] || C.muted);

  const present = attendance.filter(a => a.status === "Present").length;
  const absent  = attendance.filter(a => a.status === "Absent").length;
  const late    = attendance.filter(a => a.status === "Late").length;
  const total   = attendance.length;

  // Shared input style using theme
  const inputStyle = {
    padding:"9px 13px",
    background:C.inputBg,
    border:`1px solid ${C.border}`,
    borderRadius:9,
    color:C.text,
    fontSize:13,
    outline:"none",
  };

  return (
    <div>
      {/* Header */}
      <div className="flex-between page-header" style={{ marginBottom:22 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Attendance</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>Import daily attendance from Excel or CSV</div>
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <button onClick={downloadAttendanceTemplate}
            style={{ padding:"9px 16px", background:C.orangeSoft, border:`1px solid ${C.orange}44`, borderRadius:10, color:C.orange, fontSize:13, fontWeight:600, cursor:"pointer" }}>
            📋 Template
          </button>
          <label style={{ padding:"9px 16px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:10, color:C.accent, fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {importing ? "⏳ Importing..." : "📥 Import"}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display:"none" }} />
          </label>
        </div>
      </div>

      {/* Date Picker */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Select Date</div>
        <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
          style={{ ...inputStyle, border:`1px solid ${C.accent}`, color:C.accent, fontWeight:700, cursor:"pointer" }} />
      </div>

      {/* Message */}
      {message && (
        <div style={{ padding:"12px 16px", borderRadius:10, marginBottom:16, background:message.type==="success"?C.greenSoft:C.redSoft, border:`1px solid ${message.type==="success"?C.green:C.red}44`, color:message.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
          {message.text}
        </div>
      )}

      {/* How to use */}
      <div style={{ padding:"12px 16px", background:C.accentSoft, border:`1px solid ${C.accent}33`, borderRadius:10, marginBottom:18, fontSize:12, color:C.accent }}>
        💡 <strong>How to import:</strong> Download Template → Fill in First Name, Last Name, Phone, Date, Status (Present / Absent / Late) → Import
      </div>

      {/* Stats */}
      {total > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:18 }} className="form-grid-4">
          {[
            { label:"Present",  value:present, color:C.green },
            { label:"Absent",   value:absent,  color:C.red },
            { label:"Late",     value:late,    color:C.orange },
            { label:"Rate",     value:total>0?`${Math.round((present/total)*100)}%`:"—", color:C.accent },
          ].map((s,i) => (
            <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", textAlign:"center" }}>
              <div style={{ fontSize:22, fontWeight:800, color:s.color, fontFamily:"Georgia, serif" }}>{s.value}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:3, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden" }}>
        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:C.muted }}>⏳ Loading attendance...</div>
        ) : attendance.length === 0 ? (
          <div style={{ padding:60, textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>✅</div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>No records for this date</div>
            <div style={{ fontSize:13, color:C.muted }}>Download the template, fill in attendance and import it</div>
          </div>
        ) : (
          <div className="responsive-table">
            <table>
              <thead>
                <tr style={{ background:C.bg }}>
                  {["Volunteer","Phone","Date","Status","Imported At"].map(h => (
                    <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:C.muted, letterSpacing:"0.8px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attendance.map((a,i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, transition:"background 0.15s" }}
                    onMouseEnter={e=>e.currentTarget.style.background=C.border+"44"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{ padding:"11px 14px", fontSize:13, color:C.text, fontWeight:600 }}>{a.volunteerName}</td>
                    <td style={{ padding:"11px 14px", fontSize:12, color:C.muted }}>{a.phone}</td>
                    <td style={{ padding:"11px 14px", fontSize:12, color:C.muted }}>{a.date}</td>
                    <td style={{ padding:"11px 14px" }}>
                      <span className="vh-badge" style={{ background:`${statusColor(a.status)}22`, color:statusColor(a.status), border:`1px solid ${statusColor(a.status)}44` }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={{ padding:"11px 14px", fontSize:11, color:C.muted }}>{new Date(a.importedAt).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}